/**
 * Widget 容器组件
 *
 * 职责：
 * - 创建和管理 Shadow DOM
 * - 渲染悬浮按钮和对话窗口
 * - 处理全局事件（ESC、点击外部）
 * - 注入完整的 Tailwind CSS 到 Shadow DOM
 */

import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { LauncherButton } from '../launcher/LauncherButton';
import { ChatWidget } from '../../components/chat/ChatWidget';
import { useWidgetStore } from '../stores/widgetStore';
import { WidgetConfig } from '../config/widget.types';

// 通过 Vite 的 ?inline 查询参数，将 Tailwind CSS 作为字符串导入
// 构建时 PostCSS + Tailwind 会自动扫描所有组件生成完整的工具类 CSS
import widgetCSS from '../widget-styles.css?inline';

export interface WidgetContainerProps {
  /** Widget 配置 */
  config: WidgetConfig;
}

export const WidgetContainer: React.FC<WidgetContainerProps> = ({ config }) => {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const shadowRootRef = useRef<ShadowRoot | null>(null);
  const initialConfigRef = useRef(config);

  const {
    isWidgetOpen,
    isLauncherVisible,
    openWidget,
    closeWidget,
    initialize
  } = useWidgetStore();

  // 初始化 Shadow DOM
  useEffect(() => {
    // 查找或创建宿主元素
    let host = document.getElementById('smart-cs-root') as HTMLDivElement;
    if (!host) {
      host = document.createElement('div');
      host.id = 'smart-cs-root';
      document.body.appendChild(host);
    }

    // 创建 Shadow Root
    if (!host.shadowRoot) {
      const shadowRoot = host.attachShadow({ mode: 'open' });

      // 注入全局样式（包含 Tailwind CSS + CSS 变量）
      const style = document.createElement('style');
      style.textContent = getGlobalStyles(initialConfigRef.current);
      shadowRoot.appendChild(style);

      shadowRootRef.current = shadowRoot;
    } else {
      shadowRootRef.current = host.shadowRoot;
    }

    hostRef.current = host;

    return () => {
      // 清理（可选）
    };
  }, []);

  useEffect(() => {
    initialize(config);
  }, [config, initialize]);

  // 更新全局样式（配置变化时）
  useEffect(() => {
    if (shadowRootRef.current) {
      const style = shadowRootRef.current.querySelector('style');
      if (style) {
        style.textContent = getGlobalStyles(config);
      }
    }
  }, [config]);

  // ESC 关闭
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isWidgetOpen) {
        closeWidget();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isWidgetOpen, closeWidget]);

  // 点击外部关闭
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Element;
      if (isWidgetOpen && hostRef.current && !hostRef.current.contains(target)) {
        closeWidget();
      }
    };

    if (isWidgetOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isWidgetOpen, closeWidget]);

  // 渲染到 Shadow DOM
  if (!shadowRootRef.current) {
    return null;
  }

  return createPortal(
    <>
      {/* 悬浮按钮 */}
      {config.showLauncher !== false && (
        <LauncherButton
          visible={isLauncherVisible}
          onClick={openWidget}
          themeColor={config.themeColor}
        />
      )}

      {/* 对话窗口 */}
      <ChatWidget
        isOpen={isWidgetOpen}
        onOpenChange={(open) => open ? openWidget() : closeWidget()}
      />
    </>,
    shadowRootRef.current
  );
};

/**
 * 生成全局样式
 *
 * 包含：
 * 1. :host 下定义的 CSS 变量（主题色、尺寸、消息气泡等）
 * 2. Tailwind 自动生成的完整工具类 CSS（通过 ?inline 导入）
 */
function getGlobalStyles(config: WidgetConfig): string {
  return `
    :host {
      /* Widget 配置变量 */
      --cs-theme-color: ${config.themeColor || '#2563EB'};
      --cs-widget-width: ${config.width || 380}px;
      --cs-widget-height: ${config.height || 640}px;
      --cs-border-radius: 16px;
      --cs-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;

      /* 消息气泡颜色 */
      --bubble-user: #3b82f6;
      --bubble-ai: #f9fafb;
      --bubble-user-text: #ffffff;
      --bubble-ai-text: #171717;

      /* 文字颜色 */
      --text-primary: #171717;
      --text-secondary: #666666;
      --text-muted: #737373;
      --text-title: #171717;
      --text-placeholder: #A1A1AA;

      /* 输入框背景 */
      --bg-input: #F4F4F5;

      /* 主题强调色 */
      --accent-primary: #3b82f6;
    }

    ${widgetCSS}
  `;
}
